const TOKEN_KEY = "rmnlng.token";

// In dev we leave this empty so requests go to `/api/*` on the same origin
// and hit Vite's proxy (vite.config.ts). In prod we bake in an absolute URL
// (e.g. `https://api.rmnlng.com`) at build time so the browser calls the API
// directly on its own subdomain.
const API_BASE = import.meta.env.VITE_API_URL ?? "";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  /**
   * Stable machine-readable error code from the API response body
   * (e.g. `"auth.errors.invalid_credentials"`). Web pages match on
   * this to render the localized copy via i18n; the `message` field
   * is a non-localized English fallback for callers that don't have
   * a translation context.
   *
   * Undefined when the server didn't include a code (e.g. a 5xx
   * crash, a route that hasn't been migrated yet).
   */
  public readonly code: string | undefined;
  /**
   * Free-form auxiliary fields from the body (e.g. `retryAfterSec`
   * on a 429). Typed loosely because each error code carries its
   * own contract; the consumer narrows by code.
   */
  public readonly details: Record<string, unknown>;

  constructor(
    public status: number,
    message: string,
    options: { code?: string; details?: Record<string, unknown> } = {},
  ) {
    super(message);
    this.name = "ApiError";
    this.code = options.code;
    this.details = options.details ?? {};
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  if (!headers.has("Content-Type") && init.body) {
    headers.set("Content-Type", "application/json");
  }
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${API_BASE}/api${path}`, { ...init, headers });

  if (res.status === 401) {
    // Wipe the token only on a true credential failure (the JWT was
    // rejected). Validation errors that happen to come back with 401
    // shouldn't, so this is safe — Nest reserves 401 for JwtAuthGuard
    // failures + the explicit credential-mismatch throws.
    setToken(null);
  }

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    let code: string | undefined;
    let details: Record<string, unknown> = {};
    try {
      const body = await res.json();
      message = typeof body.message === "string" ? body.message : message;
      code = typeof body.code === "string" ? body.code : undefined;
      // Preserve every non-standard top-level field as `details` so
      // page-level handlers can read e.g. `retryAfterSec` without
      // each new error needing a bespoke ApiError subclass.
      details = { ...body };
      delete details.statusCode;
      delete details.message;
      delete details.code;
      delete details.error;
    } catch {
      /* body not JSON */
    }
    throw new ApiError(res.status, message, { code, details });
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}
